/**
 * GA4 Enhanced Cart Tracking
 * Integrates with Shopify Cart API for dynamic event tracking
 */

(function() {
  'use strict';

  // Override Shopify's cart AJAX functions to add GA4 tracking
  if (window.Shopify && window.Shopify.routes) {
    
    // Track Add to Cart via AJAX
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [url, options] = args;
      
      // Check if this is a cart add request
      if (url && url.includes('/cart/add')) {
        return originalFetch.apply(this, args)
          .then(response => {
            if (response.ok) {
              response.clone().json().then(data => {
                // Track add to cart
                if (data && data.id) {
                  const item = {
                    item_id: `shopify_${data.product_id}_${data.id}`,
                    item_name: data.product_title || data.title,
                    item_variant: data.variant_title,
                    price: data.price / 100,
                    quantity: data.quantity || 1,
                    currency: window.Shopify.currency.active || 'USD'
                  };
                  
                  // Push to dataLayer
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce: {
                      currency: item.currency,
                      value: item.price * item.quantity,
                      items: [item]
                    }
                  });
                  
                  // Send to GA4
                  if (window.gtag) {
                    gtag('event', 'add_to_cart', {
                      currency: item.currency,
                      value: item.price * item.quantity,
                      items: [item]
                    });
                  }
                }
              });
            }
            return response;
          });
      }
      
      // Check if this is a cart update request
      if (url && url.includes('/cart/update')) {
        return originalFetch.apply(this, args)
          .then(response => {
            if (response.ok) {
              response.clone().json().then(data => {
                // Track cart update
                if (data && data.items) {
                  trackCartUpdate(data);
                }
              });
            }
            return response;
          });
      }
      
      // Check if this is a cart change request
      if (url && url.includes('/cart/change')) {
        const originalBody = options && options.body ? JSON.parse(options.body) : {};
        
        return originalFetch.apply(this, args)
          .then(response => {
            if (response.ok) {
              response.clone().json().then(data => {
                // Determine if this was an add or remove
                if (originalBody.quantity === 0) {
                  // Item removed
                  trackRemoveFromCart(data, originalBody.id);
                } else {
                  // Quantity changed
                  trackCartUpdate(data);
                }
              });
            }
            return response;
          });
      }
      
      return originalFetch.apply(this, args);
    };
  }

  // Track cart updates
  function trackCartUpdate(cart) {
    if (!cart || !cart.items) return;
    
    const items = cart.items.map(item => ({
      item_id: `shopify_${item.product_id}_${item.variant_id}`,
      item_name: item.product_title || item.title,
      item_variant: item.variant_title,
      price: item.price / 100,
      quantity: item.quantity,
      currency: cart.currency || window.Shopify.currency.active || 'USD'
    }));
    
    const cartValue = cart.total_price / 100;
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_cart_updated',
      ecommerce: {
        currency: cart.currency || 'USD',
        value: cartValue,
        items: items
      }
    });
  }

  // Track remove from cart
  function trackRemoveFromCart(cart, variantId) {
    // Find the removed item in the previous cart state
    const removedItem = {
      item_id: `shopify_${variantId}`,
      currency: cart.currency || window.Shopify.currency.active || 'USD'
    };
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: removedItem.currency,
        items: [removedItem]
      }
    });
    
    if (window.gtag) {
      gtag('event', 'remove_from_cart', {
        currency: removedItem.currency,
        items: [removedItem]
      });
    }
  }

  // Track Quick View
  document.addEventListener('quick-view:open', function(event) {
    if (event.detail && event.detail.product) {
      const product = event.detail.product;
      const item = {
        item_id: `shopify_${product.id}_${product.variants[0].id}`,
        item_name: product.title,
        item_brand: product.vendor,
        price: product.price / 100,
        currency: window.Shopify.currency.active || 'USD'
      };
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'view_item_quick',
        ecommerce: {
          currency: item.currency,
          value: item.price,
          items: [item]
        }
      });
    }
  });

  // Track Wishlist Actions
  document.addEventListener('wishlist:add', function(event) {
    if (event.detail && event.detail.product) {
      const product = event.detail.product;
      const item = {
        item_id: `shopify_${product.id}`,
        item_name: product.title,
        price: product.price / 100,
        currency: window.Shopify.currency.active || 'USD'
      };
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_wishlist',
        ecommerce: {
          currency: item.currency,
          value: item.price,
          items: [item]
        }
      });
      
      if (window.gtag) {
        gtag('event', 'add_to_wishlist', {
          currency: item.currency,
          value: item.price,
          items: [item]
        });
      }
    }
  });

  // Track Product Comparison
  let comparedProducts = [];
  document.addEventListener('compare:add', function(event) {
    if (event.detail && event.detail.product) {
      comparedProducts.push(event.detail.product);
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_compare',
        product_id: event.detail.product.id,
        product_name: event.detail.product.title,
        compared_count: comparedProducts.length
      });
    }
  });

  // Track checkout initiation
  document.addEventListener('click', function(e) {
    const checkoutButton = e.target.closest('[name="checkout"], .checkout-button, [data-checkout]');
    if (checkoutButton) {
      // Get current cart data
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (cart && cart.items) {
            const items = cart.items.map(item => ({
              item_id: `shopify_${item.product_id}_${item.variant_id}`,
              item_name: item.product_title || item.title,
              item_variant: item.variant_title,
              price: item.price / 100,
              quantity: item.quantity,
              currency: cart.currency || 'USD'
            }));
            
            const cartValue = cart.total_price / 100;
            
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'begin_checkout',
              ecommerce: {
                currency: cart.currency || 'USD',
                value: cartValue,
                items: items,
                coupon: cart.discount || ''
              }
            });
            
            if (window.gtag) {
              gtag('event', 'begin_checkout', {
                currency: cart.currency || 'USD',
                value: cartValue,
                items: items,
                coupon: cart.discount || ''
              });
            }
          }
        });
    }
  });

  // Track search interactions
  const searchInput = document.querySelector('[type="search"], .search__input');
  if (searchInput) {
    let searchTimeout;
    searchInput.addEventListener('input', function(e) {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        if (e.target.value.length > 2) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'search',
            search_term: e.target.value
          });
          
          if (window.gtag) {
            gtag('event', 'search', {
              search_term: e.target.value
            });
          }
        }
      }, 1000);
    });
  }

  // Track filter usage
  document.addEventListener('change', function(e) {
    if (e.target.matches('.facet-checkbox input, .filter-input, [data-filter]')) {
      const filterType = e.target.name || e.target.dataset.filter || 'unknown';
      const filterValue = e.target.value;
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'filter_products',
        filter_type: filterType,
        filter_value: filterValue,
        filter_checked: e.target.checked
      });
    }
  });

  // Track sort changes
  const sortSelect = document.querySelector('.facet-filters__sort, [name="sort_by"]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function(e) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sort_products',
        sort_by: e.target.value
      });
    });
  }

  // Currency selector tracking
  document.addEventListener('change', function(e) {
    if (e.target.matches('[name="currency"], .currency-selector')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'currency_change',
        currency_from: window.Shopify.currency.active,
        currency_to: e.target.value
      });
    }
  });

  // Track social shares
  document.addEventListener('click', function(e) {
    const shareButton = e.target.closest('.share-button, [data-share]');
    if (shareButton) {
      const network = shareButton.dataset.share || shareButton.className.match(/share-(\w+)/)?.[1] || 'unknown';
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'share',
        method: network,
        content_type: 'product',
        item_id: window.location.pathname
      });
      
      if (window.gtag) {
        gtag('event', 'share', {
          method: network,
          content_type: 'product',
          item_id: window.location.pathname
        });
      }
    }
  });

  // Performance tracking
  if (window.performance && window.performance.timing) {
    window.addEventListener('load', function() {
      setTimeout(function() {
        const timing = window.performance.timing;
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;
        const firstByteTime = timing.responseStart - timing.navigationStart;
        
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'page_timing',
          page_load_time: pageLoadTime,
          dom_ready_time: domReadyTime,
          time_to_first_byte: firstByteTime
        });
        
        if (window.gtag) {
          gtag('event', 'timing_complete', {
            name: 'load',
            value: pageLoadTime,
            event_category: 'performance'
          });
        }
      }, 0);
    });
  }

  // Log initialization
  console.log('[GA4] Enhanced cart tracking initialized');
  
})();